import { useApolloClient } from '@apollo/client'
import { F0Box } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import {
  AttendanceSidepanelFilterTeamNamesDocument,
  FinanceBudgetDetailParticipantsTabDocument,
} from 'generated/resources/graphql'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

interface Props {
  classId: string
}

export const ParticipantsTab = ({ classId }: Props) => {
  const client = useApolloClient()
  const { navigation } = useNavigation()

  const source = useGraphqlDataCollectionSource({
    document: FinanceBudgetDetailParticipantsTabDocument,
    mapPagination: (data) => ({
      total: data.trainings.trainingClassMembershipsConnection.totalCount ?? 0,
    }),
    mapData: (data) => data.trainings.trainingClassMembershipsConnection.nodes,
    itemUrl: (item) => {
      const employeeId = item.access?.employee?.id
      if (!employeeId) return undefined

      return navigation.employees.id.fullPath({ id: employeeId })
    },
    search: { enabled: true },
    filters: {
      team: {
        type: 'in',
        label: i18n.t('trainings.trainings_revamp.event_sidepanel.filters.team'),
        options: {
          options: async () => {
            const { data } = await client.query({
              query: AttendanceSidepanelFilterTeamNamesDocument,
              fetchPolicy: 'cache-first',
            })
            return data.teams.teamsConnection.edges.map(({ node: team }) => ({
              label: team.name,
              value: team.id,
            }))
          },
        },
      },
    },
    totalItemSummary: (totalCount) =>
      i18n.tp('budgets.detail_training_budget.sidepanel.participants_count', {
        count: totalCount,
      }),
    mapVariables: ({ search, filters }) => ({
      trainingClassId: classId,
      search,
      filter: {
        ...(filters?.team?.length && { teamId: { in: filters.team } }),
      },
    }),
  })

  return (
    <F0Box display="flex" flexDirection="column" gap="xl" paddingX="sm">
      <OneDataCollection
        id="finance/budget-training-group-participants/v1"
        source={source}
        visualizations={[
          {
            type: 'table' as const,
            options: {
              columns: [
                {
                  label: i18n.t(
                    'trainings.trainings_revamp.classes_sidepanel.participants.datatable.name'
                  ),
                  render: (item) => ({
                    type: 'person' as const,
                    value: {
                      firstName: item.access?.firstName ?? '',
                      lastName: item.access?.lastName ?? '',
                      name: item.access?.fullName ?? '',
                      type: 'person' as const,
                      src: item.access?.avatar ?? undefined,
                    },
                  }),
                },
                {
                  label: i18n.t(
                    'trainings.trainings_revamp.classes_sidepanel.participants.datatable.team'
                  ),
                  render: (item) => ({
                    type: 'text' as const,
                    value: item.access?.employee?.memberships?.[0]?.team?.name ?? '',
                  }),
                },
              ],
            },
          },
        ]}
      />
    </F0Box>
  )
}
